import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountCardListStore, $transferReasonsStore } from '@lib/store';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { type AccountResponse, AccountType, TransferRequestTypeEnum } from '@shared/api';
import { OtherAccountTab, OwnAccountTab } from './widgets';
import { PageContainer, SelectAccount } from '@widgets';
import { Icon, Tabs, Text } from '@shared/widgets';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

const TABS = [
  { label: 'betweenAccounts.tabs.betweenAccounts', value: TransferRequestTypeEnum.BetweenInsigniaAccount },
  {
    label: `betweenAccounts.tabs.${PaletteUtils.isVcfcPalette ? 'otherVcfcAccount' : 'otherInsigniaAccount'}`,
    value: TransferRequestTypeEnum.OtherInsigniaAccount,
  },
];

export const PaymentBetweenAccountsPage = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');

  const accountStore = useUnit($accountCardListStore.store);

  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [tab, setTab] = useState<TransferRequestTypeEnum>(
    type ? TransferRequestTypeEnum.OtherInsigniaAccount : TransferRequestTypeEnum.BetweenInsigniaAccount,
  );

  const { t } = useTranslation('payments');

  useEffect(() => {
    Promise.allSettled([
      $accountCardListStore.events.getAccounts({ AccountType: AccountType.Payment }),
      $transferReasonsStore.events.getTransferReasons(),
    ]);
    return () => $accountCardListStore.events.clear();
  }, []);

  useEffect(() => {
    if (accountStore.data.length > 0) setAccount(accountStore.data[0]);
  }, [accountStore.data]);

  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
      return
    >
      <div className={cn(PaletteUtils.isVcfcPalette && 'rounded-t-xl bg-white p-6 dark:bg-soft-black')}>
        <Text
          className={cn('mb-6', PaletteUtils.isVcfcPalette && 'mb-0 text-[26px]')}
          weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined}
          size='2xl'
        >
          {t('betweenAccounts.title')}
        </Text>
        <div className={cn('bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'px-0')}>
          <SelectAccount
            label={t('betweenAccounts.placeholders.transferFrom')}
            loading={accountStore.loading}
            type={AccountType.Payment}
            accounts={accountStore.data}
            value={account}
            onChange={setAccount}
          />
        </div>
        <div className={cn('flex flex-col gap-2 bg-white dark:bg-soft-black', PaletteUtils.isInsigniaPalette && 'mt-6 p-6')}>
          <Tabs options={TABS.map((option) => ({ ...option, label: t(option.label) }))} value={tab} onChange={setTab} />
        </div>
      </div>
      {tab == TransferRequestTypeEnum.BetweenInsigniaAccount && <OwnAccountTab account={account} />}
      {tab == TransferRequestTypeEnum.OtherInsigniaAccount && <OtherAccountTab account={account} />}
    </PageContainer>
  );
};
